import type { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';
import type { Tone } from '../tokens';
import { Tooltip } from './overlays';

/**
 * Status presentation.
 *
 * A status is always a tone *and* a word, and wherever the space allows, an
 * icon too. Colour alone never carries the meaning — a red chip and an amber
 * chip are indistinguishable to a large minority of users (§54).
 */

const badgeVariants = cva(
  'inline-flex max-w-full items-center gap-1.5 rounded-full border font-medium',
  {
    variants: {
      tone: {
        neutral: 'border-line bg-neutral-soft text-ink-secondary',
        brand: 'border-brand-border bg-brand-soft text-brand-hover',
        success: 'border-success-border bg-success-soft text-success',
        warning: 'border-warning-border bg-warning-soft text-warning',
        danger: 'border-danger-border bg-danger-soft text-danger',
        info: 'border-info-border bg-info-soft text-info',
        accent: 'border-accent-border bg-accent-soft text-accent',
      } satisfies Record<Tone, string>,
      size: {
        sm: 'px-1.5 py-px text-micro',
        md: 'px-2 py-0.5 text-caption',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export interface BadgeProps extends VariantProps<typeof badgeVariants> {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  /**
   * The full text, for a badge whose label is truncated.
   *
   * Rendered through the `Tooltip` primitive rather than the `title` attribute:
   * the browser's own tooltip is unstyleable, appears after a delay nobody
   * controls, and never appears at all for a keyboard user. It needs a
   * `TooltipProvider` ancestor, which the app shell supplies.
   */
  title?: string;
}

export function Badge({ children, icon, tone, size, className, title }: BadgeProps) {
  const badge = (
    <span className={cn(badgeVariants({ tone, size }), className)}>
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span className="truncate">{children}</span>
    </span>
  );
  return title ? (
    <Tooltip content={title} side="top">
      {badge}
    </Tooltip>
  ) : (
    badge
  );
}

/** A dot + label, for inline status inside dense table cells. */
export function StatusDot({ tone, children }: { tone: Tone; children: ReactNode }) {
  const fill: Record<Tone, string> = {
    neutral: 'bg-ink-subtle',
    brand: 'bg-brand',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
    info: 'bg-info',
    accent: 'bg-accent',
  };
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', fill[tone])} aria-hidden />
      <span className="truncate text-base text-ink">{children}</span>
    </span>
  );
}

/** Numeric count for navigation items and queue headers. */
export function CountBadge({ count, tone = 'neutral' }: { count: number; tone?: Tone }) {
  if (count <= 0) return null;
  const styles: Record<Tone, string> = {
    neutral: 'bg-neutral-soft text-ink-secondary',
    brand: 'bg-brand-soft text-brand-hover',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger text-white',
    info: 'bg-info-soft text-info',
    accent: 'bg-accent-soft text-accent',
  };
  return (
    <span
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-micro font-semibold tabular-nums',
        styles[tone],
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
